import type { Href } from "expo-router";
import { useRouter } from "expo-router";
import { useEffect, useRef, useState } from "react";
import { Platform, Pressable, Text, View } from "react-native";
import { COLORS, FONTS } from "../../lib/brand";

import { AuthCard } from "../../components/auth/AuthCard";
import { Button, Input } from "../../components/ui";
import { supabase } from "../../lib/supabase";

const FOREST = COLORS.FOREST.hex;
const AMBER = COLORS.AMBER.hex;
const SUBTITLE = "rgba(28,43,26,0.68)";

function getResetRedirectUrl(): string {
  if (typeof window !== "undefined" && window.location?.origin) {
    return `${window.location.origin}/reset-password`;
  }
  return "https://coravu.com/reset-password";
}

function isValidEmail(s: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(s.trim());
}

export default function ForgotPasswordScreen() {
  const router = useRouter();
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sentTo, setSentTo] = useState<string | null>(null);

  const focusedOnce = useRef(false);
  useEffect(() => {
    // Web-only autofocus — the Input primitive doesn't expose a ref API,
    // so we cheat via document.querySelector on the first paint. No-op on
    // native (the keyboard would pop immediately, which is jarring).
    if (Platform.OS !== "web" || focusedOnce.current) return;
    focusedOnce.current = true;
    requestAnimationFrame(() => {
      const el = document.querySelector<HTMLInputElement>("input[type='email']");
      el?.focus();
    });
  }, []);

  async function onSubmit() {
    setError(null);
    const trimmed = email.trim();
    if (!trimmed) {
      setError("Enter your email to continue.");
      return;
    }
    if (!isValidEmail(trimmed)) {
      setError("That email doesn't look quite right.");
      return;
    }
    setBusy(true);
    try {
      const { error: resetErr } = await supabase.auth.resetPasswordForEmail(trimmed, {
        redirectTo: getResetRedirectUrl(),
      });
      if (resetErr) {
        // Supabase's password-reset endpoint intentionally returns 200 for
        // non-existent emails (anti-enumeration). If it does surface an
        // error here, surface it directly — most often a rate-limit hit.
        setError(resetErr.message);
        return;
      }
      setSentTo(trimmed);
    } finally {
      setBusy(false);
    }
  }

  if (sentTo) {
    return (
      <AuthCard>
        <Text
          className="mb-2 text-[10px] font-bold uppercase tracking-widest-3"
          style={{ fontFamily: FONTS.bodyBold, color: AMBER, letterSpacing: 2 }}
        >
          CHECK YOUR INBOX
        </Text>
        <Text className="text-[28px] leading-tight text-text" style={{ fontFamily: FONTS.display }}>
          On its way.
        </Text>
        <Text
          className="mt-3 text-[14px] leading-6"
          style={{ fontFamily: FONTS.bodyLight, color: SUBTITLE }}
        >
          We sent a reset link to{" "}
          <Text style={{ fontFamily: FONTS.bodyMedium, color: FOREST }}>{sentTo}</Text>.{" "}
          It should arrive in the next minute or two.
        </Text>
        <Text
          className="mt-4 text-[13px] leading-5"
          style={{ fontFamily: FONTS.bodyLight, color: SUBTITLE }}
        >
          Didn't get it? Check your spam folder, or reach out to{" "}
          <Text style={{ fontFamily: FONTS.bodyMedium, color: AMBER }}>hello@coravu.com</Text>.
        </Text>

        <View className="mt-7">
          <Button
            variant="primary"
            onPress={() => {
              setSentTo(null);
              setEmail("");
            }}
          >
            Try a different email
          </Button>
        </View>

        <Pressable
          onPress={() => router.replace("/login" as Href)}
          className="mt-4 self-center"
          style={Platform.OS === "web" ? ({ cursor: "pointer" } as const) : {}}
        >
          <Text
            className="text-center text-[12px]"
            style={{ fontFamily: FONTS.bodyMedium, color: AMBER }}
          >
            ← Back to sign in
          </Text>
        </Pressable>
      </AuthCard>
    );
  }

  return (
    <AuthCard>
      <Text
        className="mb-2 text-[10px] font-bold uppercase tracking-widest-3"
        style={{ fontFamily: FONTS.bodyBold, color: AMBER, letterSpacing: 2 }}
      >
        FORGOT PASSWORD
      </Text>
      <Text className="text-[28px] leading-tight text-text" style={{ fontFamily: FONTS.display }}>
        Reset your password.
      </Text>
      <Text className="mt-2 text-[14px] leading-6" style={{ fontFamily: FONTS.bodyLight, color: SUBTITLE }}>
        Enter the email you signed up with — we'll send you a link to set a new password.
      </Text>

      <View className="mt-6">
        <Input
          label="Email"
          value={email}
          onChangeText={setEmail}
          placeholder="you@example.com"
          keyboardType="email-address"
          autoComplete="email"
          textContentType="emailAddress"
          returnKeyType="send"
          onSubmitEditing={() => void onSubmit()}
        />
      </View>

      {error ? (
        <Text
          className="mb-1 mt-3 text-center text-sm text-red-600"
          style={{ fontFamily: FONTS.body }}
        >
          {error}
        </Text>
      ) : null}

      <View className="mt-4">
        <Button variant="primary" loading={busy} onPress={() => void onSubmit()}>
          {busy ? "Sending reset link…" : "Send reset link"}
        </Button>
      </View>

      <Pressable
        onPress={() => router.replace("/login" as Href)}
        className="mt-4 self-center"
        style={Platform.OS === "web" ? ({ cursor: "pointer" } as const) : {}}
      >
        <Text
          className="text-center text-[12px]"
          style={{ fontFamily: FONTS.bodyMedium, color: AMBER }}
        >
          ← Back to sign in
        </Text>
      </Pressable>

      <Text
        className="mt-6 text-center text-[11px] leading-4"
        style={{ fontFamily: FONTS.bodyLight, color: "rgba(28,43,26,0.50)" }}
      >
        Didn't receive an email? Check spam, or reach out to{" "}
        <Text style={{ fontFamily: FONTS.bodyMedium, color: AMBER }}>hello@coravu.com</Text>.
      </Text>
    </AuthCard>
  );
}
